import React, { memo, Fragment, useCallback } from 'react'
import { useTheme } from '@mui/material'
import PropTypes from 'prop-types'
import { RESOLUTION_SIZES } from '@v4/constants/images'
import PageMetaData from '@v4/utils/data/PageMetaData'
import { useSectionIndex } from '@v4/components/common/SectionItem'
import AppStore from '@v4/store/appStore'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageData from '@v4/utils/data/PageData'
import useStyles from './Picture.styled'

const Picture = ({ className, src, alt, srcSet }) => {
	const theme = useTheme()
	const classes = useStyles()
	const sectionIndex = useSectionIndex()
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const pageType = PageBuilderSelector.useType()

	const loading = (isBuilderMode || !PageData.isTypeResultPdf(pageType)) && sectionIndex > 0 ? 'lazy' : 'eager'

	const handleLoad = useCallback(() => {
		if (!isBuilderMode) {
			window.dispatchEvent(new CustomEvent('image-loaded'))
		}
	}, [isBuilderMode])

	return (
		<picture className={classes.root}>
			{srcSet &&
				srcSet
					.map(({ key, url, value }) => {
						const media = key === 'none' ? 'all' : theme.breakpoints.down(key).replace('@media ', '')
						return (
							<Fragment key={key}>
								{RESOLUTION_SIZES.map(size => (
									<source
										key={size}
										media={PageMetaData.imageResolutionSizeFrom(media, size)}
										srcSet={PageMetaData.imageThumbUrlFrom(url, Math.round(value * size))}
									/>
								))}
								{key !== 'none' && (
									<source media={media} srcSet={PageMetaData.imageThumbUrlFrom(url, value)} />
								)}
							</Fragment>
						)
					})
					.reverse()}
			<img
				loading={loading}
				className={className}
				src={src && PageMetaData.imageThumbUrlFrom(src, srcSet?.[0]?.value)}
				alt={alt || ''}
				width="100%"
				height="100%"
				onLoad={handleLoad}
				onError={handleLoad}
			/>
		</picture>
	)
}

Picture.propTypes = {
	className: PropTypes.string,
	src: PropTypes.string,
	alt: PropTypes.string,
	srcSet: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'none']).isRequired,
			url: PropTypes.string.isRequired,
			value: PropTypes.number.isRequired,
		}),
	),
}

export default memo(Picture)
